import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  Timestamp,
  writeBatch,
  increment
} from 'firebase/firestore';
import { db } from '../firebase';
import { Tag, TagUsage, BulkTagOperation, TagFilter } from '../types/tag';
import { assertNotImpersonating } from '../utils/impersonationGuard';

class TagService {
  private readonly collectionName = 'tags';

  // Create a new tag
  async createTag(userId: string, tagData: Omit<Tag, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'usageCount'>): Promise<string> {
    assertNotImpersonating('create tag');
    try {
      console.log('Creating tag with data:', { userId, tagData });
      
      // Validate required fields
      if (!userId) {
        throw new Error('User ID is required');
      }
      if (!tagData.name || !tagData.name.trim()) {
        throw new Error('Tag name is required');
      }
      if (!tagData.color) {
        throw new Error('Tag color is required');
      }

      const tag: Omit<Tag, 'id'> = {
        ...tagData,
        userId,
        usageCount: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        isSystem: false, // Ensure isSystem is set
        // Remove undefined description field
        ...(tagData.description && { description: tagData.description })
      };

      console.log('Tag object to create:', tag);

      const docRef = await addDoc(collection(db, this.collectionName), tag);
      console.log('Tag created successfully with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error in createTag:', error);
      throw error;
    }
  }

  // Get all tags for a user
  async getUserTags(userId: string): Promise<Tag[]> {
    const q = query(
      collection(db, this.collectionName),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    const tags = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Tag[];
    
    // Sort by usage count (desc) then name (asc) on the client side
    return tags.sort((a, b) => {
      if (b.usageCount !== a.usageCount) {
        return b.usageCount - a.usageCount;
      }
      return a.name.localeCompare(b.name);
    });
  }

  // Get a specific tag by ID
  async getTag(tagId: string): Promise<Tag | null> {
    const docRef = doc(db, this.collectionName, tagId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Tag;
    }
    return null;
  }

  // Update a tag
  async updateTag(tagId: string, updates: Partial<Omit<Tag, 'id' | 'userId' | 'createdAt' | 'usageCount'>>): Promise<void> {
    assertNotImpersonating('update tag');
    const docRef = doc(db, this.collectionName, tagId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  }

  // Update tag usage count
  async updateTagUsageCount(tagId: string, usageCount: number): Promise<void> {
    assertNotImpersonating('update tag usage');
    const docRef = doc(db, this.collectionName, tagId);
    await updateDoc(docRef, {
      usageCount,
      updatedAt: Timestamp.now()
    });
  }

  // Delete a tag
  async deleteTag(tagId: string): Promise<void> {
    assertNotImpersonating('delete tag');
    const docRef = doc(db, this.collectionName, tagId);
    await deleteDoc(docRef);
  }

  // System tags are disabled - users create their own tags

  // Get tag usage statistics
  async getTagUsage(userId: string): Promise<TagUsage[]> {
    const tags = await this.getUserTags(userId);
    
    // This would typically involve a more complex query to count contacts per tag
    // For now, we'll return the usage count from the tag document
    return tags.map(tag => ({
      tagId: tag.id,
      tagName: tag.name,
      contactCount: tag.usageCount,
      lastUsed: tag.updatedAt
    }));
  }

  // Search tags by name
  async searchTags(userId: string, searchTerm: string): Promise<Tag[]> {
    const allTags = await this.getUserTags(userId);
    return allTags.filter(tag => 
      tag.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  // Bulk tag operations
  async bulkTagContacts(userId: string, operation: BulkTagOperation): Promise<void> {
    assertNotImpersonating('bulk tag contacts');
    const batch = writeBatch(db);
    
    // Update contact documents
    operation.contactIds.forEach(contactId => {
      const contactRef = doc(db, 'contacts', contactId);
      
      if (operation.operation === 'add') {
        // Add tags to existing tags
        batch.update(contactRef, {
          tags: increment(0), // This would need to be handled differently in a real implementation
          updatedAt: Timestamp.now()
        });
      } else if (operation.operation === 'remove') {
        // Remove specific tags
        batch.update(contactRef, {
          updatedAt: Timestamp.now()
        });
      } else if (operation.operation === 'replace') {
        // Replace all tags
        batch.update(contactRef, {
          tags: operation.tagIds,
          updatedAt: Timestamp.now()
        });
      }
    });

    // Update tag usage counts
    operation.tagIds.forEach(tagId => {
      const tagRef = doc(db, this.collectionName, tagId);
      
      if (operation.operation === 'add') {
        batch.update(tagRef, {
          usageCount: increment(operation.contactIds.length),
          updatedAt: Timestamp.now()
        });
      } else if (operation.operation === 'remove') {
        batch.update(tagRef, {
          usageCount: increment(-operation.contactIds.length),
          updatedAt: Timestamp.now()
        });
      }
    });

    await batch.commit();
  }

  // Get contacts by tag filter
  async getContactsByTagFilter(userId: string, filter: TagFilter): Promise<string[]> {
    // This would typically involve a complex query
    // For now, we'll return an empty array as this would need to be implemented
    // based on how contacts are stored and indexed
    return [];
  }

  // Validate tag name
  validateTagName(name: string): { isValid: boolean; error?: string } {
    if (!name || name.trim().length === 0) {
      return { isValid: false, error: 'Tag name is required' };
    }
    
    if (name.length > 50) {
      return { isValid: false, error: 'Tag name must be 50 characters or less' };
    }
    
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(name)) {
      return { isValid: false, error: 'Tag name can only contain letters, numbers, spaces, hyphens, and underscores' };
    }
    
    return { isValid: true };
  }

  // Check if tag name is unique for user
  async isTagNameUnique(userId: string, name: string, excludeTagId?: string): Promise<boolean> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        where('name', '==', name.trim())
      );
      
      const querySnapshot = await getDocs(q);
      const existingTags = querySnapshot.docs.map(doc => doc.id);
      
      // If excludeTagId is provided, filter it out
      if (excludeTagId) {
        return !existingTags.some(tagId => tagId !== excludeTagId);
      }
      
      return existingTags.length === 0;
    } catch (error) {
      console.error('Error checking tag name uniqueness:', error);
      // If there's an error, assume it's not unique to be safe
      return false;
    }
  }
}

export const tagService = new TagService();

// Debug function to test tag creation (remove in production)
if (typeof window !== 'undefined') {
  (window as any).testTagCreation = async (userId: string) => {
    try {
      console.log('Testing tag creation for user:', userId);
      const result = await tagService.createTag(userId, {
        name: 'Test Tag',
        color: '#3B82F6',
        description: 'Test tag for debugging'
      });
      console.log('Tag created successfully:', result);
      return result;
    } catch (error) {
      console.error('Tag creation failed:', error);
      throw error;
    }
  };
}
